import { io, Socket } from "socket.io-client";

// Détection de l'URL du serveur API & Socket.io
export const getApiUrl = (): string => {
    try {
        const customUrl = localStorage.getItem('admin_socket_server_url');
        if (customUrl && customUrl.trim()) return customUrl.trim().replace(/\/$/, '');
        if (typeof window !== 'undefined') {
            const hostname = window.location.hostname;
            const port = window.location.port;
            if (port === '5173' || port === '5174' || port === '3000' || hostname === 'localhost' || hostname === '127.0.0.1') {
                return `http://${hostname}:3000`;
            }
            return window.location.origin;
        }
    } catch (e) {
        console.warn('[API] Could not determine origin:', e);
    }
    return '';
};

export const getSocketUrl = (): string => {
    return getApiUrl();
};

export const formatImageUrl = (url?: string): string => {
    if (!url) return '';
    if (url.startsWith('data:') || url.startsWith('http://') || url.startsWith('https://')) {
        return url;
    }
    if (url.startsWith('/uploads/')) {
        const baseUrl = getApiUrl();
        return baseUrl ? `${baseUrl}${url}` : url;
    }
    return url;
};

export const compressImage = (file: File, maxWidth = 1200, maxHeight = 1200, quality = 0.8): Promise<string> => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (event) => {
            const img = new Image();
            img.onload = () => {
                let width = img.width;
                let height = img.height;

                if (width > maxWidth || height > maxHeight) {
                    if (width > height) {
                        height = Math.round((height * maxWidth) / width);
                        width = maxWidth;
                    } else {
                        width = Math.round((width * maxHeight) / height);
                        height = maxHeight;
                    }
                }

                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = height;

                const ctx = canvas.getContext('2d');
                if (!ctx) {
                    resolve(event.target?.result as string);
                    return;
                }

                ctx.drawImage(img, 0, 0, width, height);
                const format = file.type === 'image/png' ? 'image/png' : 'image/jpeg';
                const dataUrl = canvas.toDataURL(format, quality);
                resolve(dataUrl);
            };
            img.onerror = () => resolve(event.target?.result as string);
            img.src = event.target?.result as string;
        };
        reader.onerror = (err) => reject(err);
        reader.readAsDataURL(file);
    });
};

type EventCallback = (...args: any[]) => void;
const eventListeners: Map<string, Set<EventCallback>> = new Map();

// Canal BroadcastChannel pour synchronisation instantanée multi-onglets
const broadcastBus: BroadcastChannel | null = typeof window !== 'undefined' && 'BroadcastChannel' in window
    ? new BroadcastChannel('astro_socket_bus')
    : null;

if (broadcastBus) {
    broadcastBus.onmessage = (event) => {
        if (event && event.data && event.data.type) {
            const { type, payload } = event.data;
            const listeners = eventListeners.get(type);
            if (listeners) {
                listeners.forEach(cb => {
                    try {
                        cb(...(Array.isArray(payload) ? payload : [payload]));
                    } catch (err) {
                        console.error(`[SocketBus] Erreur handler ${type}:`, err);
                    }
                });
            }
        }
    };
}

if (typeof window !== 'undefined') {
    window.addEventListener('storage', (e) => {
        if (e.key === 'astro_sync_event' && e.newValue) {
            try {
                const { type, payload } = JSON.parse(e.newValue);
                const listeners = eventListeners.get(type);
                if (listeners) {
                    listeners.forEach(cb => {
                        try {
                            cb(...(Array.isArray(payload) ? payload : [payload]));
                        } catch (err) {
                            console.error(`[StorageSync] Erreur handler ${type}:`, err);
                        }
                    });
                }
            } catch (err) {}
        }
    });
}

// Initialisation Socket.io client
let realSocket: Socket | null = null;
try {
    const serverUrl = getSocketUrl();
    if (serverUrl) {
        realSocket = io(serverUrl, {
            transports: ["polling", "websocket"],
            reconnectionAttempts: Infinity,
            reconnectionDelay: 1000,
            reconnectionDelayMax: 5000,
            timeout: 15000,
            autoConnect: true,
            withCredentials: true
        });

        realSocket.on("connect", () => {
            console.log("[Socket.io] [OK] Connecté avec succès au serveur:", serverUrl, "ID:", realSocket?.id);
        });

        realSocket.on("connect_error", (err) => {
            console.warn("[Socket.io] Reconnexion en cours...", err.message);
        });
    }
} catch (err) {
    console.warn("[Socket.io] Erreur init:", err);
}

const activeSocketListeners = new Set<string>();

export const socket = {
    get connected() {
        return realSocket ? realSocket.connected : true;
    },
    get id() {
        return realSocket?.id || 'local-client-' + Math.random().toString(36).substring(2, 9);
    },

    on: (event: string, callback: EventCallback) => {
        if (!eventListeners.has(event)) {
            eventListeners.set(event, new Set());
        }
        eventListeners.get(event)!.add(callback);

        if (realSocket && !activeSocketListeners.has(event)) {
            activeSocketListeners.add(event);
            realSocket.on(event, (...args: any[]) => {
                const cbs = eventListeners.get(event);
                if (cbs) {
                    cbs.forEach(cb => {
                        try {
                            cb(...args);
                        } catch (e) {
                            console.error(`[Socket] Error in listener for "${event}":`, e);
                        }
                    });
                }
            });
        }
    },

    off: (event: string, callback?: EventCallback) => {
        if (callback) {
            eventListeners.get(event)?.delete(callback);
        } else {
            eventListeners.delete(event);
        }
    },

    emit: (event: string, ...args: any[]) => {
        // 1. Envoyer toujours sur le serveur Socket.io
        if (realSocket) {
            realSocket.emit(event, ...args);
        }

        
        // 2. Synchroniser avec tous les autres onglets locaux (messages de chat, notifications, promos)
        if (broadcastBus) {
            try {
                broadcastBus.postMessage({
                    type: event,
                    payload: args.length === 1 ? args[0] : args
                });
            } catch (e) {}
        }

        // 3. Storage fallback
        try {
            localStorage.setItem('astro_sync_event', JSON.stringify({
                type: event,
                payload: args.length === 1 ? args[0] : args,
                timestamp: Date.now()
            }));
        } catch (e) {}
    },

    connect: () => {
        if (realSocket) {
            realSocket.connect();
        }
    },

    disconnect: () => {
        if (realSocket) {
            realSocket.disconnect();
        }
    }
} as any;

export const getSocket = () => socket;
